import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  BarChart3, ChevronLeft, Loader2, Download, Calendar,
  TrendingUp, Navigation, Euro, FileText, Route
} from 'lucide-react';

const ReportsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [summary, setSummary] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchSummary = useCallback(async () => {
    const token = localStorage.getItem('optitrajet_token');
    if (!token) {
      navigate('/optitrajet/login');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/optitrajet/reports/summary?date_from=${dateRange.from}&date_to=${dateRange.to}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      }
    } catch (err) {
      console.error('Fetch summary error:', err);
    } finally {
      setLoading(false);
    }
  }, [API_URL, navigate, dateRange]);

  useEffect(() => {
    setMounted(true);
    fetchSummary();
  }, [fetchSummary]);

  const handleExport = async (format) => {
    setExporting(true);
    const token = localStorage.getItem('optitrajet_token');
    
    try {
      const res = await fetch(
        `${API_URL}/api/optitrajet/reports/export?format=${format}&date_from=${dateRange.from}&date_to=${dateRange.to}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `optitrajet_export_${new Date().toISOString().split('T')[0]}.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Export error:', err);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F8FAFC' }}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 lg:pb-8" style={{ background: '#F8FAFC', fontFamily: "'Inter', sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/optitrajet/dashboard')}
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <ChevronLeft className="h-5 w-5 text-slate-600" />
            </button>
            <h1 style={{ fontFamily: "'Outfit', sans-serif" }} className="text-xl font-bold text-slate-800">
              Rapports
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Date Range Selector */}
        <Card className={`mb-6 border-0 shadow-lg transition-all duration-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="flex items-center gap-2 flex-1">
                <Calendar className="h-5 w-5 text-slate-400" />
                <input
                  type="date"
                  value={dateRange.from}
                  onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                  className="h-10 px-3 rounded-xl border border-slate-200 bg-white text-sm"
                />
                <span className="text-slate-400">→</span>
                <input
                  type="date"
                  value={dateRange.to}
                  onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                  className="h-10 px-3 rounded-xl border border-slate-200 bg-white text-sm"
                />
              </div>
              <Button onClick={fetchSummary} variant="outline" className="rounded-xl">
                Actualiser
              </Button>
            </div>
          </CardContent>
        </Card>

        {summary && (
          <>
            {/* Summary Stats */}
            <div className={`grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 transition-all duration-500 delay-100 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-indigo-50">
                <CardContent className="p-4 text-center">
                  <Route className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-blue-700" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {summary.summary.total_trips}
                  </p>
                  <p className="text-xs text-blue-600">Trajets</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-green-50">
                <CardContent className="p-4 text-center">
                  <Navigation className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-emerald-700" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {summary.summary.total_distance_km}
                  </p>
                  <p className="text-xs text-emerald-600">km parcourus</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm bg-gradient-to-br from-violet-50 to-purple-50">
                <CardContent className="p-4 text-center">
                  <TrendingUp className="h-6 w-6 text-violet-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-violet-700" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {summary.summary.total_revenue}€
                  </p>
                  <p className="text-xs text-violet-600">Revenus</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-50 to-orange-50">
                <CardContent className="p-4 text-center">
                  <Euro className="h-6 w-6 text-amber-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-amber-700" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {summary.summary.total_expenses}€
                  </p>
                  <p className="text-xs text-amber-600">Dépenses</p>
                </CardContent>
              </Card>
            </div>

            {/* Net Profit & Mileage Allowance */}
            <Card className={`mb-6 border-0 shadow-lg overflow-hidden transition-all duration-500 delay-200 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <CardContent className="p-0">
                <div className="grid md:grid-cols-2">
                  <div className="p-6 bg-gradient-to-br from-green-500 to-emerald-600 text-white">
                    <p className="text-sm opacity-90 mb-1">Bénéfice net</p>
                    <p className="text-4xl font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {summary.summary.net_profit}€
                    </p>
                    <p className="text-xs opacity-75 mt-2">
                      Revenus ({summary.summary.total_revenue}€) - Dépenses ({summary.summary.total_expenses}€)
                    </p>
                  </div>
                  <div className="p-6 bg-white">
                    <p className="text-sm text-slate-500 mb-1">Indemnités kilométriques</p>
                    <p className="text-4xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {summary.summary.mileage_allowance}€
                    </p>
                    <p className="text-xs text-slate-400 mt-2">
                      Barème fiscal français (0,603€/km ≤5000km)
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Expenses Breakdown */}
            {summary.expenses_breakdown && Object.keys(summary.expenses_breakdown).length > 0 && (
              <Card className={`mb-6 border-0 shadow-lg transition-all duration-500 delay-300 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                <CardContent className="p-6">
                  <h3 style={{ fontFamily: "'Outfit', sans-serif" }} className="text-lg font-semibold text-slate-800 mb-4">
                    Répartition des dépenses
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(summary.expenses_breakdown).map(([cat, data]) => {
                      const total = summary.summary.total_expenses || 1;
                      const percent = ((data.total / total) * 100).toFixed(1);
                      const categoryNames = {
                        fuel: 'Carburant',
                        toll: 'Péages',
                        parking: 'Parking',
                        maintenance: 'Entretien',
                        insurance: 'Assurance',
                        other: 'Autre'
                      };
                      return (
                        <div key={cat}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-slate-600">{categoryNames[cat] || cat}</span>
                            <span className="font-medium text-slate-800">{data.total}€ ({percent}%)</span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all"
                              style={{ width: `${percent}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Export Buttons */}
            <Card className={`border-0 shadow-lg transition-all duration-500 delay-400 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <CardContent className="p-6">
                <h3 style={{ fontFamily: "'Outfit', sans-serif" }} className="text-lg font-semibold text-slate-800 mb-4">
                  Exporter les données
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <Button 
                    onClick={() => handleExport('csv')}
                    disabled={exporting}
                    variant="outline"
                    className="h-14 rounded-xl border-slate-200 hover:bg-slate-50"
                  >
                    {exporting ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        <Download className="h-5 w-5 mr-2" />
                        Export CSV
                      </>
                    )}
                  </Button>
                  <Button 
                    onClick={() => handleExport('json')}
                    disabled={exporting}
                    variant="outline"
                    className="h-14 rounded-xl border-slate-200 hover:bg-slate-50"
                  >
                    {exporting ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        <FileText className="h-5 w-5 mr-2" />
                        Export JSON
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-slate-400 mt-3 text-center">
                  Compatible avec Excel, Google Sheets et les logiciels comptables
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
};

export default ReportsPage;
